(() => {
  const ROOT_SELECTOR = '#MainContent';
  const SKIP_SELECTOR = [
    '.cpp-configurator',
    '.cpp-designer',
    '.cpp-nutrition-generator',
    '.cpp-nutrition-builder',
    '.cpp-qr-generator',
    '.cpp-qr-builder',
    '.cpp-barcode-generator',
    '.cpp-barcode-builder',
    '.cpp-visual-component-pouch-test',
    '.cpp-visual-component-worksheet',
    '[role="dialog"]',
    '.modal',
    '.popup',
    '.cart-drawer',
    '[id*="CartDrawer"]',
  ].join(',');

  const PRIMARY = [
    /\border (?:your )?pouches?\b/i,
    /\bbuild (?:&|and) price\b/i,
    /\bbuild your pouch\b/i,
    /\bget (?:a )?live custom quote\b/i,
    /\bopen (?:the )?pouch configurator\b/i,
    /\buse (?:the )?pouch configurator\b/i,
    /\bdesign your pouch\b/i,
  ];
  const SECONDARY = [
    /\bsee product recommendations?\b/i,
    /\blearn more(?: about pouches)?\b/i,
    /\bexplore (?:pouch options|materials(?: and finishes)?|resources|custom pouches)\b/i,
    /\bsee all pouch options\b/i,
    /\bcompare (?:all )?(?:materials(?: and finishes)?|pouch options)\b/i,
    /\bdownload comparison guide\b/i,
    /\b(?:create|generate) (?:a |your )?barcode\b/i,
    /\b(?:make|create) (?:a |your )?(?:qr code label|qr label)\b/i,
    /\bopen (?:the )?(?:label generator|nutrition facts label generator)\b/i,
    /\bexplore .+(?:packaging|bags|pouch options)\b/i,
    /\bback to (?:blog|resources)\b/i,
  ];
  const TERTIARY = [
    /\bask (?:a )?question\b/i,
    /\bask for (?:design )?help\b/i,
    /\bcontact (?:us|our team)\b/i,
    /\bget help\b/i,
    /\btalk to (?:us|our team|an expert)\b/i,
    /\bsee how it works\b/i,
    /\bsee the steps\b/i,
  ];

  function normalizedText(element) {
    return String(element.getAttribute('aria-label') || element.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isAskQuestion(element) {
    return /\bask (?:a )?question\b/i.test(normalizedText(element));
  }

  function tierFor(element) {
    if (element.matches('[data-cpp-cta="primary"]')) return 'primary';
    if (element.matches('[data-cpp-cta="secondary"]')) return 'secondary';
    if (element.matches('[data-cpp-cta="tertiary"]')) return 'tertiary';
    const text = normalizedText(element);
    if (!text) return null;
    if (TERTIARY.some((pattern) => pattern.test(text))) return 'tertiary';
    if (PRIMARY.some((pattern) => pattern.test(text))) return 'primary';
    if (SECONDARY.some((pattern) => pattern.test(text))) return 'secondary';
    return null;
  }

  function isCtaCandidate(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (!element.matches('a,button')) return false;
    if (element.closest(SKIP_SELECTOR)) return false;
    if (element.matches('.cpp-back-to-top,.skip-to-content-link')) return false;
    if (isAskQuestion(element)) return true;
    const className = typeof element.className === 'string' ? element.className : '';
    return /(button|btn|cta|action)/i.test(className)
      || element.matches('.cpp-content-cta a,.cpp-home__text-button,.cpp-mf__button,.cpp-visual-component-food-matrix__link,[data-cpp-cta]');
  }

  function removeLegacyPresentation(element) {
    [
      'background',
      'background-color',
      'border-color',
      'box-shadow',
      'color',
      'text-decoration',
    ].forEach((property) => element.style.removeProperty(property));
  }

  function styleCtas(scope = document) {
    const root = scope.matches?.(ROOT_SELECTOR) ? scope : scope.querySelector?.(ROOT_SELECTOR);
    if (!root) return;
    const candidates = root.matches('a,button') ? [root] : root.querySelectorAll('a,button');
    candidates.forEach((element) => {
      if (!isCtaCandidate(element)) return;
      const tier = tierFor(element);
      if (!tier) return;
      removeLegacyPresentation(element);
      element.classList.remove('cpp-cta-button--primary', 'cpp-cta-button--secondary', 'cpp-cta-button--tertiary');
      element.classList.add('cpp-cta-button', `cpp-cta-button--${tier}`);
      element.dataset.cppCtaTier = tier;
      if (isAskQuestion(element)) {
        const nativeDrawer = document.querySelector('#PopupModal-message');
        const drawer = nativeDrawer || document.querySelector('#CppQuestionDrawer');
        element.dataset.cppQuestionTrigger = 'true';
        element.setAttribute('aria-haspopup', 'dialog');
        if (drawer?.id) element.setAttribute('aria-controls', drawer.id);
      }
    });
  }

  function openQuestionDrawer(trigger) {
    if (trigger.closest('modal-opener[data-modal="#PopupModal-message"]')) return;
    const drawer = document.querySelector('#PopupModal-message') || document.querySelector('#CppQuestionDrawer');
    if (!drawer || typeof drawer.show !== 'function') return;
    drawer.show(trigger);
  }

  function handleQuestionClick(event) {
    const trigger = event.target.closest?.('a,button');
    if (!trigger || !isAskQuestion(trigger)) return;
    if (trigger.closest('modal-opener[data-modal="#PopupModal-message"]')) return;
    event.preventDefault();
    openQuestionDrawer(trigger);
  }

  function init() {
    styleCtas();
    document.addEventListener('click', handleQuestionClick);
    const submittedDrawer = document.querySelector('#CppQuestionDrawer [data-cpp-question-form-state]');
    if (submittedDrawer) {
      window.requestAnimationFrame(() => {
        const drawer = document.querySelector('#CppQuestionDrawer');
        if (drawer && typeof drawer.show === 'function') drawer.show(document.body);
      });
    }
    const main = document.querySelector(ROOT_SELECTOR);
    if (!main || main.dataset.cppCtaObserverReady) return;
    main.dataset.cppCtaObserverReady = 'true';
    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.addedNodes.length)) return;
      window.requestAnimationFrame(() => styleCtas());
    });
    observer.observe(main, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', () => styleCtas());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

/* CPP_BULK_APPROVAL_RUNTIME_START */
window.cppBulkApprovalDispatch=window.cppBulkApprovalDispatch||function(event,root){
  if(!root)return;
  if(!root.__cppBulkApproval){
  root.__cppBulkApproval=(function(){
  var form=root.querySelector('form');
  var steps=Array.prototype.slice.call(root.querySelectorAll('[data-cpp-step-title]'));
  var count=root.querySelector('[data-cpp-progress-count]');
  var incompleteList=root.querySelector('[data-cpp-incomplete-list]');
  var actionStatus=root.querySelector('[data-cpp-action-status]');

  function clean(value){return String(value||'').replace(/\\s+/g,' ').trim()}
  function value(name){var field=form.elements.namedItem(name);return field?clean(field.value):''}
  function escapeHtml(value){return String(value||'').replace(/[&<>"']/g,function(character){return{'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[character]})}
  function statusFor(step){var checked=step.querySelector('[data-cpp-status]:checked');return checked?checked.value:'Not started'}
  function resize(textarea){textarea.style.height='auto';textarea.style.height=Math.max(textarea.scrollHeight,96)+'px'}
  function updateSummary(){
    var approved=0;
    var incomplete=[];
    steps.forEach(function(step,index){var status=statusFor(step);if(status==='Approved'){approved+=1}else{incomplete.push({number:index+1,title:step.getAttribute('data-cpp-step-title'),id:step.id,status:status})}});
    count.textContent=approved+' of '+steps.length+' checks approved; '+incomplete.length+' still open.';
    incompleteList.innerHTML='';
    if(!incomplete.length){var done=document.createElement('li');done.textContent='All checks are approved. The final owner must still authorize release.';incompleteList.appendChild(done);return}
    incomplete.forEach(function(item){var li=document.createElement('li');var badge=document.createElement('span');badge.className='cpp-visual-component-bulk-approval__incomplete-number';badge.textContent=item.number;var link=document.createElement('a');link.href='#'+item.id;link.textContent=item.title;li.appendChild(badge);li.appendChild(link);li.appendChild(document.createTextNode(' - '+item.status));incompleteList.appendChild(li)})
  }
  function collect(){return{generatedAt:new Date().toLocaleString(),steps:steps.map(function(step){var slug=step.id.replace('cpp-worksheet-step-','');return{title:step.getAttribute('data-cpp-step-title'),status:statusFor(step),decision:value(slug+'-decision'),owner:value(slug+'-owner'),notes:value(slug+'-notes')}}),summary:{issue:value('incomplete-summary'),owner:value('incomplete-owner'),deadline:value('incomplete-deadline'),release:value('incomplete-disposition')}}}
  function htmlDocument(data){
    var sections=data.steps.map(function(step,index){return'<section><h2><span>'+String(index+1)+'</span>'+escapeHtml(step.title)+'</h2><p class="status"><strong>Status:</strong> '+escapeHtml(step.status)+'</p><dl><dt>Decision or requirement</dt><dd>'+escapeHtml(step.decision||'Not provided')+'</dd><dt>Assigned owner</dt><dd>'+escapeHtml(step.owner||'Not provided')+'</dd><dt>Open issues, evidence or notes</dt><dd>'+escapeHtml(step.notes||'Not provided')+'</dd></dl></section>'}).join('');
    return '<!doctype html><html><head><meta charset="utf-8"><title>CPP completed bulk pouch approval worksheet</title><style>@page{margin:.55in}*{box-sizing:border-box}body{margin:0;color:#18233c;font:11pt/1.45 Arial,sans-serif}header{border-top:12px solid #00a6ce;padding:18px 0;border-bottom:2px solid #e90070}h1{font-size:24pt;margin:0 0 6px}header p{margin:0;color:#596277}section{break-inside:avoid;padding:16px 0;border-bottom:1px solid #cfd5df}h2{display:flex;gap:10px;align-items:center;margin:0 0 8px;font-size:15pt}h2 span{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;color:#fff;background:#e90070;font-size:10pt}p.status{margin:0 0 8px}dl{margin:0}dt{font-weight:700;margin-top:7px}dd{margin:2px 0 0;white-space:pre-wrap}.summary{margin-top:20px;padding:16px;background:#f5f7fa}.footer{margin-top:18px;color:#596277;font-size:9pt}</style></head><body><header><h1>Bulk pouch approval worksheet</h1><p>Completed in the browser at CustomPrintedPouches.com on '+escapeHtml(data.generatedAt)+'.</p></header>'+sections+'<section class="summary"><h2>Outstanding work and release conditions</h2><dl><dt>Issue or decision still needed</dt><dd>'+escapeHtml(data.summary.issue||'Not provided')+'</dd><dt>Owner of the next action</dt><dd>'+escapeHtml(data.summary.owner||'Not provided')+'</dd><dt>Decision deadline</dt><dd>'+escapeHtml(data.summary.deadline||'Not provided')+'</dd><dt>Release condition or exception</dt><dd>'+escapeHtml(data.summary.release||'Not provided')+'</dd></dl></section><p class="footer">A completed worksheet is a planning record, not an order submission or signature.</p></body></html>'
  }
  function ascii(value){return String(value||'').normalize('NFKD').replace(/[\\u0300-\\u036f]/g,'').replace(/[\\u2018\\u2019]/g,"'").replace(/[\\u201C\\u201D]/g,'"').replace(/[\\u2013\\u2014]/g,'-').replace(/[^\\x20-\\x7E]/g,'')}
  function pdfEscape(value){return ascii(value).replace(/\\/g,'\\\\').replace(/\(/g,'\\(').replace(/\)/g,'\\)')}
  function wrap(value,max){var words=ascii(value||'Not provided').split(/\\s+/);var lines=[];var line='';words.forEach(function(word){var candidate=(line+' '+word).trim();if(candidate.length<=max){line=candidate}else{if(line)lines.push(line);line=word}});if(line)lines.push(line);return lines.length?lines:['Not provided']}
  function makePdf(data){
    var lines=[{text:'Bulk pouch approval worksheet',size:18,bold:true,color:'0.09 0.14 0.24'},{text:'Completed at CustomPrintedPouches.com - '+data.generatedAt,size:8,color:'0.35 0.39 0.47',gap:14}];
    data.steps.forEach(function(step,index){lines.push({text:String(index+1)+'. '+step.title,size:12,bold:true,color:'0.91 0 0.44',gap:14});lines.push({text:'Status: '+step.status,size:9,bold:true});lines.push({text:'Decision or requirement:',size:8,bold:true,gap:9});wrap(step.decision,88).forEach(function(text){lines.push({text:text,size:8.5,indent:10})});lines.push({text:'Assigned owner: '+(step.owner||'Not provided'),size:8.5,bold:true,gap:8});lines.push({text:'Open issues, evidence or notes:',size:8,bold:true,gap:8});wrap(step.notes,88).forEach(function(text){lines.push({text:text,size:8.5,indent:10})});lines.push({rule:true,gap:11})});
    lines.push({text:'Outstanding work and release conditions',size:13,bold:true,color:'0.09 0.14 0.24',gap:16});[['Issue or decision still needed',data.summary.issue],['Owner of the next action',data.summary.owner],['Decision deadline',data.summary.deadline],['Release condition or exception',data.summary.release]].forEach(function(item){lines.push({text:item[0]+':',size:8,bold:true,gap:9});wrap(item[1],88).forEach(function(text){lines.push({text:text,size:8.5,indent:10})})});
    var pages=[];var page=[];var y=730;lines.forEach(function(line){var needed=line.rule?line.gap||10:(line.gap||0)+(line.size||9)+4;if(y-needed<52){pages.push(page);page=[];y=730}page.push(line);y-=needed});if(page.length)pages.push(page);
    var objects=[];function add(object){objects.push(object);return objects.length}
    add('<< /Type /Catalog /Pages 2 0 R >>');add('');var fontRegular=add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');var fontBold=add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');var kids=[];
    pages.forEach(function(pageLines,pageIndex){var content='0 0.65 0.81 rg 0 780 153 12 re f\\n0.91 0 0.44 rg 153 780 153 12 re f\\n1 0.79 0.02 rg 306 780 153 12 re f\\n0.38 0.21 0.96 rg 459 780 153 12 re f\\n';var cursor=752;pageLines.forEach(function(line){cursor-=line.gap||0;if(line.rule){content+='0.81 0.84 0.88 RG 42 '+cursor+' m 570 '+cursor+' l S\\n';cursor-=line.gap||10;return}var size=line.size||9;var color=line.color||'0.09 0.14 0.24';content+='BT /'+(line.bold?'F2':'F1')+' '+size+' Tf '+color+' rg '+(42+(line.indent||0))+' '+cursor+' Td ('+pdfEscape(line.text)+') Tj ET\\n';cursor-=size+4});content+='BT /F1 7 Tf 0.35 0.39 0.47 rg 42 27 Td (CustomPrintedPouches.com) Tj ET\\nBT /F1 7 Tf 0.35 0.39 0.47 rg 520 27 Td (Page '+String(pageIndex+1)+' of '+String(pages.length)+') Tj ET\\n';var stream=add('<< /Length '+String(content.length)+' >>\\nstream\\n'+content+'endstream');var pageObject=add('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 '+fontRegular+' 0 R /F2 '+fontBold+' 0 R >> >> /Contents '+stream+' 0 R >>');kids.push(pageObject+' 0 R')});objects[1]='<< /Type /Pages /Count '+String(kids.length)+' /Kids ['+kids.join(' ')+'] >>';
    var pdf='%PDF-1.4\\n';var offsets=[0];objects.forEach(function(object,index){offsets.push(pdf.length);pdf+=String(index+1)+' 0 obj\\n'+object+'\\nendobj\\n'});var xref=pdf.length;pdf+='xref\\n0 '+String(objects.length+1)+'\\n0000000000 65535 f \\n';for(var i=1;i<offsets.length;i+=1)pdf+=String(offsets[i]).padStart(10,'0')+' 00000 n \\n';pdf+='trailer\\n<< /Size '+String(objects.length+1)+' /Root 1 0 R >>\\nstartxref\\n'+String(xref)+'\\n%%EOF';return new Blob([pdf],{type:'application/pdf'})
  }
  function setActionStatus(message){if(actionStatus)actionStatus.textContent=message}
  function downloadCompleted(){try{setActionStatus('Preparing your completed worksheet PDF...');var blob=makePdf(collect());var url=URL.createObjectURL(blob);var link=document.createElement('a');link.href=url;link.download='cpp-bulk-pouch-approval-completed.pdf';link.style.display='none';document.body.appendChild(link);link.click();setTimeout(function(){link.remove();URL.revokeObjectURL(url)},1500);setActionStatus('Your completed worksheet PDF has been downloaded.')}catch(error){setActionStatus('We could not create the PDF. Please use Print completed worksheet instead.');console.error('CPP worksheet PDF error',error)}}
  function printCompleted(){var popup=window.open('','_blank');if(!popup){setActionStatus('Please allow pop-ups, then try Print completed worksheet again.');return}popup.document.open();popup.document.write(htmlDocument(collect()));popup.document.close();setActionStatus('The worksheet-only print view is ready.');setTimeout(function(){popup.focus();popup.print()},300)}
  function clearForm(){if(!window.confirm('Clear every browser entry in this worksheet? This cannot be undone.'))return;form.reset();form.querySelectorAll('textarea').forEach(resize);updateSummary();setActionStatus('Browser entries cleared.')}
  root.querySelectorAll('textarea').forEach(resize);
  updateSummary();
  return{resize:resize,updateSummary:updateSummary,downloadCompleted:downloadCompleted,printCompleted:printCompleted,clearForm:clearForm}
  }());
  ['click','input','change'].forEach(function(type){root.addEventListener(type,function(nextEvent){window.cppBulkApprovalDispatch(nextEvent,root)})});
  }
  var api=root.__cppBulkApproval;
  if(event&&event.type==='input'&&event.target&&event.target.matches&&event.target.matches('textarea'))api.resize(event.target);
  if(event&&event.type==='input'||event&&event.type==='change')api.updateSummary();
  var action=event&&event.target&&event.target.closest?event.target.closest('[data-cpp-worksheet-action]'):null;
  if(!action||!root.contains(action)||event.type!=='click')return;
  event.preventDefault();
  var name=action.getAttribute('data-cpp-worksheet-action');
  if(name==='download-completed')api.downloadCompleted();
  if(name==='print-completed')api.printCompleted();
  if(name==='clear')api.clearForm();
};
(function(){
  var selector='.cpp-visual-component-bulk-approval';
  function start(root){
    if(!root||root.dataset.cppBulkApprovalReady)return;
    window.cppBulkApprovalDispatch({type:'init',target:root},root);
    root.dataset.cppBulkApprovalReady='true';
  }
  function startAll(scope){
    if(!scope)return;
    if(scope.matches&&scope.matches(selector))start(scope);
    if(scope.querySelectorAll)scope.querySelectorAll(selector).forEach(start);
  }
  function init(){
    startAll(document);
    if(!document.body||document.body.dataset.cppBulkApprovalObserverReady)return;
    document.body.dataset.cppBulkApprovalObserverReady='true';
    new MutationObserver(function(mutations){
      mutations.forEach(function(mutation){mutation.addedNodes.forEach(function(node){if(node.nodeType===1)startAll(node)})});
    }).observe(document.body,{childList:true,subtree:true});
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});
  else init();
}());
/* CPP_BULK_APPROVAL_RUNTIME_END */
